import React from "react";
import moment from "moment";
// material ui
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/styles";
// jazasoft
import { PageFooter, BackButton, Button } from "jazasoft";
import Table from "jazasoft/lib/mui/components/Table";
// icons
import ExcelIcon from "mdi-material-ui/FileExcel";
// locals
import Report from "./Report";
import CardHeader from "../../../components/CardHeader";
import { MacroReportId } from "../../../constants";
// helpers
import useQuery from "../../../hooks/useQuery.js";
import useMutation from "../../../hooks/useMutation.js";

const useStyles = makeStyles({
  root: {
    margin: "1.5em 0"
  },
  nowrapTable: {
    whiteSpace: "nowrap"
  },
  headerCell: {
    fontWeight: "bold",
    fontFamily: "Roboto, sans-serif",
    padding: "1em 2em",
    "&:last-child": {
      padding: "1em 2em",
      fontWeight: "bold",
      fontFamily: "Roboto, sans-serif"
    }
  },
  rowCell: {
    padding: "1em 2em",
    "&:last-child": {
      padding: "1em 2em"
    }
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  }
});

const getColumns = [
  { dataKey: "status", title: "STATUS" },
  { dataKey: "ordersCount", title: "ORDERS COUNT", align: "right" }
];

const getRows = orders => {
  if (!orders) return;
  let rows =
    orders &&
    Object.keys(orders)
      .map(key => ({
        status: key.split("-")[1],
        ordersCount: orders[key],
        id: key.split("-")[0]
      }))
      .sort((a, b) => a.id - b.id);

  rows.push({ status: "Total", ordersCount: rows.reduce((acc, row) => acc + row.ordersCount, 0) });
  return rows;
};

const getSearchText = ({ start, end }, dateKey) => {
  let filters = [];
  if (start) {
    start = !isNaN(start) ? moment(start).valueOf() : moment(start, "YYYY-MM-DD").valueOf();
    filters.push(`${dateKey}=ge=${start}`);
  }
  if (end) {
    end = !isNaN(end) ? moment(end).valueOf() : moment(end, "YYYY-MM-DD").valueOf();
    filters.push(`${dateKey}=lt=${end}`);
  }
  return filters.join(";");
};

const OrderStatusReportView = props => {
  const classes = useStyles();
  const { filter = {} } = props;

  const { data: orders } = useQuery({
    url: "orders",
    params: { action: "order_status", search: getSearchText(filter, "order.createdAt") } //search: order.createdAt=ge={start};order.createdAt=lt={end}
  });

  const [mutate, { loading }] = useMutation({ rawResponse: true });

  // On Excel download click
  const onExcelDownload = () => {
    const options = {
      url: "orders",
      method: "get",
      responseType: "arraybuffer",
      params: {
        action: "order_status",
        search: getSearchText(filter, "orderCreatedAt") + `;state!='Closed'`, //search: orderCreatedAt=ge={start};orderCreatedAt=lt={end};state!='Closed'
        download: true
      }
    };
    mutate(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var filename = header ? header.match(/filename="(.+)"/)[1] : "ORDER STATUS.xlsx";

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
      }
    });
  };

  return (
    <div className={classes.root}>
      <Card>
        <CardHeader title="Order Status" />
        <Divider />
        <CardContent style={{ padding: 0 }}>
          <Table
            classes={{ table: classes.nowrapTable, headerCell: classes.headerCell, rowCell: classes.rowCell }}
            columns={getColumns}
            rows={getRows(orders)}
            cellStyle={({ record }) =>
              record.status === "Total"
                ? {
                    fontWeight: "bold"
                  }
                : {}
            }
          />
        </CardContent>
      </Card>
      <PageFooter className={classes.displayCenter}>
        <BackButton variant="contained" />
        <Button
          label="Download"
          variant="contained"
          style={{ color: "green", marginLeft: "1em" }}
          disabled={loading}
          onClick={onExcelDownload}>
          {loading && <CircularProgress size={20} thickness={2} />}
          {!loading && <ExcelIcon />}
        </Button>
      </PageFooter>
    </div>
  );
};

const OrderStatusReport = props => (
  <Report
    title="Order Status Report"
    filterKeys={["start", "end"]}
    defaultMonths={1}
    macroReportId={MacroReportId.ORDER_STATUS}
    {...props}>
    <OrderStatusReportView {...props} />
  </Report>
);

export default OrderStatusReport;
